import { useCallback } from 'react';
import { isArray, isNil } from 'lodash';
// Import Types
import type { PartnerSettingsPermissionsKeys } from 'types';
// Import Store
import { useAppSelector } from 'store';
import { selectUserPermissionsList } from 'store/slices/userData';
import { selectPartnerSettings } from 'store/slices/partnerSettings';
// Import Enums
import { SubscriptionSectionEnum } from 'enums';

const useHasPermission = () => {
    const userAllPermissionList = useAppSelector(selectUserPermissionsList);
    const partnerSettings = useAppSelector(selectPartnerSettings);
    const { Subscription } = partnerSettings ?? {};

    const isLoadingPartnerSettings = !(partnerSettings && 'Id' in partnerSettings);

    const hasPermission = useCallback(
        (permissions: string | string[], isAllMandatory = false): boolean => {
            if (isNil(userAllPermissionList)) return false;

            if (isArray(permissions)) {
                if (isAllMandatory) {
                    const existsPermissions: string[] = [];
                    permissions.forEach((permission) => {
                        userAllPermissionList.includes(permission) && existsPermissions.push(permission);
                    });
                    return existsPermissions.length === permissions.length;
                } else {
                    return permissions.some((permission) => userAllPermissionList.includes(permission));
                }
            } else {
                return userAllPermissionList.includes(permissions);
            }
        },
        [userAllPermissionList],
    );

    const hasPartnerSettings = useCallback(
        (settingsKey: PartnerSettingsPermissionsKeys) => {
            return !!partnerSettings?.[settingsKey];
        },
        [partnerSettings],
    );

    const hasSubscriptionSection = useCallback(
        (section?: SubscriptionSectionEnum, isTrueInitially = true): boolean => {
            if (isNil(section)) {
                return true;
            }
            if (isNil(Subscription?.Sections)) {
                return isTrueInitially;
            }
            return section in Subscription!.Sections;
        },
        [Subscription],
    );

    const hasSubscriptionCJBlock = useCallback(
        (blockName?: string): boolean =>
            isNil(blockName) || isNil(Subscription?.WorkflowBlocks)
                ? true
                : !!Subscription?.WorkflowBlocks.includes(blockName),
        [Subscription],
    );

    const hasSubscriptionPlaceholder = useCallback(
        (id?: number): boolean =>
            isNil(id) || isNil(Subscription?.Placeholders) ? true : !!Subscription?.Placeholders.includes(id),
        [Subscription],
    );

    return {
        isLoadingPartnerSettings,
        hasPermission,
        hasPartnerSettings,
        hasSubscriptionSection,
        hasSubscriptionCJBlock,
        hasSubscriptionPlaceholder,
    };
};

export default useHasPermission;
